import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, Copy } from "lucide-react";
import api, { imgUrl, formatApiError } from "../../lib/api";
import { toast } from "sonner";

export default function AdminProducts() {
  const [products, setProducts] = useState(null);
  const [stockEdits, setStockEdits] = useState({});

  const load = () => api.get("/admin/products").then((r) => setProducts(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/products/${p.id}`);
      toast.success("Product deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const duplicate = async (p) => {
    try {
      await api.post(`/admin/products/${p.id}/duplicate`);
      toast.success("Product duplicated (hidden — edit & make live)");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const saveStock = async (p) => {
    try {
      await api.patch(`/admin/products/${p.id}/stock`, { stock: stockEdits[p.id] });
      toast.success("Stock updated");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!products) return <div className="h-40 bg-muted animate-pulse" />;

  return (
    <div data-testid="admin-products">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-serif text-3xl font-semibold">Products <span className="text-sm text-muted-foreground font-sans font-normal">({products.length})</span></h1>
        <Link to="/admin/products/new" className="bg-[#111] text-[#FAF9F6] px-5 py-2.5 text-xs uppercase tracking-widest hover:bg-gold hover:text-[#111] transition-colors duration-300 flex items-center gap-2" data-testid="add-product-button">
          <Plus strokeWidth={1.5} size={14} /> Add Product
        </Link>
      </div>

      {products.length === 0 ? (
        <div className="bg-white border border-black/10 p-12 text-center" data-testid="no-products">
          <p className="font-serif text-xl">No products yet</p>
          <p className="text-sm text-muted-foreground mt-2">Click "Add Product" to upload your first product with photos.</p>
        </div>
      ) : (
        <div className="bg-white border border-black/10 divide-y divide-black/5">
          {products.map((p) => (
            <div key={p.id} className="flex items-center gap-4 p-4" data-testid={`admin-product-${p.slug}`}>
              <div className="w-14 h-14 border border-black/10 shrink-0 bg-muted">
                {p.images?.[0] && <img src={imgUrl(p.images[0])} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{p.name}</p>
                <p className="text-xs text-muted-foreground">{p.category_name} {p.sale_price ? `· Sale ₹${p.sale_price}` : ""} {p.bestseller ? "· Bestseller" : ""} {p.new_arrival ? "· New" : ""}</p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <input type="number" min="0" value={stockEdits[p.id] ?? p.stock}
                  onChange={(e) => setStockEdits((s) => ({ ...s, [p.id]: e.target.value }))}
                  className={`w-16 border px-2 py-1.5 text-xs outline-none focus:border-gold ${p.stock <= 5 ? "border-destructive text-destructive" : "border-black/15"}`}
                  data-testid={`stock-input-${p.slug}`} />
                <button onClick={() => saveStock(p)} className="text-[10px] uppercase tracking-widest border-b border-black hover:border-gold hover:text-gold transition-colors duration-300" data-testid={`stock-save-${p.slug}`}>Save</button>
              </div>
              <p className="text-sm font-semibold shrink-0">₹{p.price.toLocaleString("en-IN")}</p>
              <span className={`text-[10px] uppercase tracking-widest px-2 py-1 shrink-0 ${p.active ? "bg-green-100 text-green-900" : "bg-muted text-muted-foreground"}`}>{p.active ? "Live" : "Hidden"}</span>
              <button onClick={() => duplicate(p)} className="p-2 hover:text-gold transition-colors duration-300 shrink-0" data-testid={`duplicate-${p.slug}`} aria-label="Duplicate"><Copy strokeWidth={1.5} size={16} /></button>
              <Link to={`/admin/products/${p.id}/edit`} className="p-2 hover:text-gold transition-colors duration-300 shrink-0" data-testid={`edit-${p.slug}`} aria-label="Edit"><Pencil strokeWidth={1.5} size={16} /></Link>
              <button onClick={() => remove(p)} className="p-2 hover:text-destructive transition-colors duration-300 shrink-0" data-testid={`delete-${p.slug}`} aria-label="Delete"><Trash2 strokeWidth={1.5} size={16} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
